import Phaser from 'phaser';
import {
  PERSONALITIES,
  type PersonalityId,
} from '../../game/ai/personality.js';
import type { MapDefinition } from '../../game/types.js';
import type { BetaGameMode } from '../../store/createBetaGame.js';
import { Button } from './Button.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

const BACKDROP_ALPHA = 0.78;
const BACKDROP_DEPTH = 1000;

const PANEL_WIDTH = 620;
const PANEL_RADIUS = 12;
const PANEL_DEPTH = 1001;

const MAP_CARD_WIDTH = 170;
const MAP_CARD_HEIGHT = 120;
const MAP_CARD_RADIUS = 8;
const MAP_CARD_GAP = 18;

const PERSONALITY_CHIP_WIDTH = 130;
const PERSONALITY_CHIP_HEIGHT = 40;
const PERSONALITY_CHIP_GAP = 8;
const PERSONALITY_CHIP_RADIUS = 6;

const VISIBILITY_CHIP_WIDTH = 200;
const VISIBILITY_CHIP_HEIGHT = 50;
const VISIBILITY_CHIP_GAP = 16;
const VISIBILITY_CHIP_RADIUS = 6;

const SEATS_CHIP_WIDTH = 110;
const SEATS_CHIP_HEIGHT = 50;
const SEATS_CHIP_GAP = 14;
const SEATS_CHIP_RADIUS = 6;
const SEATS_OPTIONS: readonly number[] = [2, 3, 4];

const BUTTON_GAP = 24;
const BUTTON_WIDTH = 240;
const BUTTON_HEIGHT = 64;

export interface BetaConfigSelection {
  readonly mapId: string;
  /** Length 2; only meaningful when mode === '2v2-ai'. */
  readonly aiPersonalities: readonly PersonalityId[];
  /** True iff the host opted into listing this lobby in the public-
   *  games browser. Default false (private / invite-link only). */
  readonly isPublic: boolean;
  /** Auto-start threshold the host picked. FFA: 2, 3, or 4 (default
   *  4). 2v2-ai: always 2 (the mode is fixed at 2 humans). */
  readonly targetSeats: number;
}

export interface BetaConfigModalOptions {
  readonly mode: BetaGameMode;
  readonly maps: readonly MapDefinition[];
  readonly defaultMapId: string;
  readonly defaultPersonalities: readonly [PersonalityId, PersonalityId];
  readonly onCancel: () => void;
  readonly onCreate: (selection: BetaConfigSelection) => void;
}

/**
 * Pre-create config screen for the beta multiplayer flow. Host taps
 * FREE-FOR-ALL or TEAM UP vs AI on Home; this modal opens; once they've
 * chosen settings and tapped CREATE & SHARE the parent calls
 * createBetaGame() with the selection.
 *
 * Joiners never see this — they land in JoinScene from the shared URL.
 *
 * Two sections:
 *   - MAP picker (always shown) — 3 cards, one per shipped map. The
 *     selected card gets a cyan border + tinted fill.
 *   - AI OPPONENTS picker (only when mode === '2v2-ai') — two rows,
 *     one per AI seat, each with three personality chips.
 *
 * Panel height adjusts to whichever sections are visible so the FFA
 * variant stays compact and the 2v2-ai variant has room for its rows.
 */
export class BetaConfigModal {
  private readonly scene: Phaser.Scene;
  private readonly opts: BetaConfigModalOptions;
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private readonly buttons: Button[] = [];
  private selectedMapId: string;
  private selectedPersonalities: [PersonalityId, PersonalityId];
  private selectedIsPublic = false;
  private selectedTargetSeats: number;
  // Stored so re-renders (after a selection tap) can wipe and redraw
  // only the picker sections without touching backdrop/panel/title.
  private mapCardObjects: Phaser.GameObjects.GameObject[] = [];
  private personalityChipObjects: Phaser.GameObjects.GameObject[] = [];
  private visibilityChipObjects: Phaser.GameObjects.GameObject[] = [];
  private seatsChipObjects: Phaser.GameObjects.GameObject[] = [];
  private mapSectionY = 0;
  private visibilitySectionY = 0;
  private seatsSectionY = 0;
  private personalitySectionY = 0;
  private destroyed = false;

  constructor(scene: Phaser.Scene, opts: BetaConfigModalOptions) {
    this.scene = scene;
    this.opts = opts;
    this.selectedMapId = opts.defaultMapId;
    this.selectedPersonalities = [
      opts.defaultPersonalities[0],
      opts.defaultPersonalities[1],
    ];
    // FFA defaults to 4 humans (longest fill window for public games,
    // matches the historical cap). 2v2-ai is locked at 2 humans by
    // mode definition and has no UI for changing it.
    this.selectedTargetSeats = opts.mode === '2v2-ai' ? 2 : 4;

    this.draw();
  }

  // --- Layout + render ---

  private draw(): void {
    const isTeamMode = this.opts.mode === '2v2-ai';
    // Panel height varies by mode. Both modes get the visibility
    // block; FFA additionally gets a SEATS picker (target_seats =
    // 2/3/4); 2v2-AI instead gets the two AI personality rows.
    const visibilityBlockHeight = 110;
    const seatsBlockHeight = isTeamMode ? 0 : 110;
    const personalityBlockHeight = isTeamMode ? 220 : 0;
    const panelHeight =
      520 +
      visibilityBlockHeight +
      seatsBlockHeight +
      personalityBlockHeight;

    const cx = DESIGN_WIDTH / 2;
    const cy = DESIGN_HEIGHT / 2;
    const panelTop = cy - panelHeight / 2;

    // Backdrop swallows taps on the rest of the page while modal is up.
    const backdrop = this.scene.add.rectangle(
      cx,
      cy,
      DESIGN_WIDTH,
      DESIGN_HEIGHT,
      0x000000,
      BACKDROP_ALPHA
    );
    backdrop.setDepth(BACKDROP_DEPTH);
    backdrop.setInteractive();
    this.objects.push(backdrop);

    // Panel body.
    const panelBg = this.scene.add.graphics();
    panelBg.fillStyle(COLORS.bgPanelInt, 1);
    panelBg.fillRoundedRect(
      cx - PANEL_WIDTH / 2,
      panelTop,
      PANEL_WIDTH,
      panelHeight,
      PANEL_RADIUS
    );
    panelBg.lineStyle(2, COLORS.accentAmberInt, 1);
    panelBg.strokeRoundedRect(
      cx - PANEL_WIDTH / 2,
      panelTop,
      PANEL_WIDTH,
      panelHeight,
      PANEL_RADIUS
    );
    panelBg.setDepth(PANEL_DEPTH);
    this.objects.push(panelBg);

    // Title — BETA SETUP in amber to match the home BETA stickers.
    const title = this.scene.add
      .text(cx, panelTop + 50, 'Beta Setup', {
        fontFamily: FONT_HEADER,
        fontSize: '34px',
        color: COLORS.accentAmberHex,
        fontStyle: '700',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(title);

    const subtitle = this.scene.add
      .text(
        cx,
        panelTop + 90,
        isTeamMode ? 'TEAM UP vs AI' : 'FREE-FOR-ALL',
        {
          fontFamily: FONT_MONO,
          fontSize: '14px',
          color: COLORS.textSecondary,
        }
      )
      .setLetterSpacing(3)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(subtitle);

    // Section: MAP
    this.mapSectionY = panelTop + 140;
    const mapHeader = this.scene.add
      .text(cx - PANEL_WIDTH / 2 + 40, this.mapSectionY, 'MAP', {
        fontFamily: FONT_HEADER,
        fontSize: '14px',
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setLetterSpacing(4)
      .setOrigin(0, 0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(mapHeader);

    this.renderMapCards();

    // Section: VISIBILITY (always shown). Private = invite link only
    // (current behavior); Public = lobby appears in the Open Games
    // browser for anyone signed in.
    this.visibilitySectionY = this.mapSectionY + MAP_CARD_HEIGHT + 70;
    const visibilityHeader = this.scene.add
      .text(
        cx - PANEL_WIDTH / 2 + 40,
        this.visibilitySectionY,
        'VISIBILITY',
        {
          fontFamily: FONT_HEADER,
          fontSize: '14px',
          color: COLORS.textMuted,
          fontStyle: '700',
        }
      )
      .setLetterSpacing(4)
      .setOrigin(0, 0.5)
      .setDepth(PANEL_DEPTH);
    this.objects.push(visibilityHeader);
    this.renderVisibilityChips();

    // Section: SEATS (FFA only — 2v2-ai is fixed at 2 humans, no UI)
    if (!isTeamMode) {
      this.seatsSectionY = this.visibilitySectionY + 110;
      const seatsHeader = this.scene.add
        .text(
          cx - PANEL_WIDTH / 2 + 40,
          this.seatsSectionY,
          'SEATS',
          {
            fontFamily: FONT_HEADER,
            fontSize: '14px',
            color: COLORS.textMuted,
            fontStyle: '700',
          }
        )
        .setLetterSpacing(4)
        .setOrigin(0, 0.5)
        .setDepth(PANEL_DEPTH);
      this.objects.push(seatsHeader);
      this.renderSeatsChips();
    }

    // Section: AI OPPONENTS (2v2-ai only)
    if (isTeamMode) {
      this.personalitySectionY =
        this.visibilitySectionY + 110;
      const aiHeader = this.scene.add
        .text(
          cx - PANEL_WIDTH / 2 + 40,
          this.personalitySectionY,
          'AI OPPONENTS',
          {
            fontFamily: FONT_HEADER,
            fontSize: '14px',
            color: COLORS.textMuted,
            fontStyle: '700',
          }
        )
        .setLetterSpacing(4)
        .setOrigin(0, 0.5)
        .setDepth(PANEL_DEPTH);
      this.objects.push(aiHeader);

      this.renderPersonalityRows();
    }

    // Buttons — Cancel left, Create & Share right.
    const buttonsY = panelTop + panelHeight - 60;
    const cancelBtn = new Button(this.scene, {
      x: cx - (BUTTON_WIDTH / 2 + BUTTON_GAP / 2),
      y: buttonsY,
      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
      text: 'Cancel',
      onTap: () => {
        this.destroy();
        this.opts.onCancel();
      },
    });
    const createBtn = new Button(this.scene, {
      x: cx + (BUTTON_WIDTH / 2 + BUTTON_GAP / 2),
      y: buttonsY,
      width: BUTTON_WIDTH,
      height: BUTTON_HEIGHT,
      text: 'Create & Share',
      style: 'primary',
      onTap: () => {
        this.opts.onCreate({
          mapId: this.selectedMapId,
          aiPersonalities: this.selectedPersonalities,
          isPublic: this.selectedIsPublic,
          targetSeats: this.selectedTargetSeats,
        });
        // Parent owns destroy timing here — they call destroy() once
        // the in-flight POST settles so the modal stays up if creation
        // fails. (HomeScene.handleBetaCreate does this.)
      },
    });
    cancelBtn.setDepth(PANEL_DEPTH);
    createBtn.setDepth(PANEL_DEPTH);
    this.buttons.push(cancelBtn, createBtn);
  }

  // --- Map cards ---

  private renderMapCards(): void {
    // Wipe the previous render's card objects (re-render on selection).
    for (const o of this.mapCardObjects) o.destroy();
    this.mapCardObjects = [];

    const cx = DESIGN_WIDTH / 2;
    const cardsRowY = this.mapSectionY + 30 + MAP_CARD_HEIGHT / 2;
    const totalWidth =
      this.opts.maps.length * MAP_CARD_WIDTH +
      (this.opts.maps.length - 1) * MAP_CARD_GAP;
    const startX = cx - totalWidth / 2 + MAP_CARD_WIDTH / 2;

    this.opts.maps.forEach((map, i) => {
      const x = startX + i * (MAP_CARD_WIDTH + MAP_CARD_GAP);
      this.drawMapCard(x, cardsRowY, map);
    });
  }

  private drawMapCard(x: number, y: number, map: MapDefinition): void {
    const selected = map.id === this.selectedMapId;

    const cardBg = this.scene.add.graphics();
    const fillColor = selected ? COLORS.buttonBgPrimaryInt : COLORS.bgElevatedInt;
    cardBg.fillStyle(fillColor, 1);
    cardBg.fillRoundedRect(
      x - MAP_CARD_WIDTH / 2,
      y - MAP_CARD_HEIGHT / 2,
      MAP_CARD_WIDTH,
      MAP_CARD_HEIGHT,
      MAP_CARD_RADIUS
    );
    cardBg.lineStyle(
      selected ? 2 : 1,
      selected ? COLORS.accentCyanInt : COLORS.borderSubtleInt,
      1
    );
    cardBg.strokeRoundedRect(
      x - MAP_CARD_WIDTH / 2,
      y - MAP_CARD_HEIGHT / 2,
      MAP_CARD_WIDTH,
      MAP_CARD_HEIGHT,
      MAP_CARD_RADIUS
    );
    cardBg.setDepth(PANEL_DEPTH);
    this.mapCardObjects.push(cardBg);

    // Hit area on a transparent rectangle. Graphics objects in Phaser
    // don't take pointer events naturally; a rectangle with alpha 0
    // gives us a clean tap target without the visual bleed.
    const hit = this.scene.add
      .rectangle(x, y, MAP_CARD_WIDTH, MAP_CARD_HEIGHT, 0x000000, 0)
      .setInteractive({ cursor: 'pointer' })
      .setDepth(PANEL_DEPTH + 1);
    hit.on('pointerdown', () => {
      if (this.selectedMapId === map.id) return;
      this.selectedMapId = map.id;
      this.renderMapCards();
    });
    this.mapCardObjects.push(hit);

    const nameText = this.scene.add
      .text(x, y - 28, map.name.toUpperCase(), {
        fontFamily: FONT_HEADER,
        fontSize: '24px',
        color: selected ? COLORS.accentCyanHex : COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(4)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.mapCardObjects.push(nameText);

    // Stack tile + region counts on separate lines so Terra (25 tiles ·
    // 5 regions) doesn't overflow the 170px card width. align:center
    // centers both rows on the card; setLineSpacing tightens the gap
    // since 12px text default line-height feels too loose for a chip.
    const tCount = map.territories.length;
    const rCount = map.regions.length;
    const meta = this.scene.add
      .text(x, y + 18, `${tCount} TILES\n${rCount} REGIONS`, {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: COLORS.textMuted,
        align: 'center',
      })
      .setLetterSpacing(2)
      .setLineSpacing(2)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.mapCardObjects.push(meta);
  }

  // --- Visibility chips ---

  private renderVisibilityChips(): void {
    for (const o of this.visibilityChipObjects) o.destroy();
    this.visibilityChipObjects = [];

    const cx = DESIGN_WIDTH / 2;
    const rowY = this.visibilitySectionY + 50;
    const totalWidth =
      2 * VISIBILITY_CHIP_WIDTH + VISIBILITY_CHIP_GAP;
    const firstChipX = cx - totalWidth / 2 + VISIBILITY_CHIP_WIDTH / 2;

    this.drawVisibilityChip(firstChipX, rowY, false, 'PRIVATE');
    this.drawVisibilityChip(
      firstChipX + VISIBILITY_CHIP_WIDTH + VISIBILITY_CHIP_GAP,
      rowY,
      true,
      'PUBLIC'
    );
  }

  private drawVisibilityChip(
    x: number,
    y: number,
    value: boolean,
    label: string
  ): void {
    const selected = this.selectedIsPublic === value;

    const chipBg = this.scene.add.graphics();
    chipBg.fillStyle(
      selected ? COLORS.buttonBgPrimaryInt : COLORS.bgElevatedInt,
      1
    );
    chipBg.fillRoundedRect(
      x - VISIBILITY_CHIP_WIDTH / 2,
      y - VISIBILITY_CHIP_HEIGHT / 2,
      VISIBILITY_CHIP_WIDTH,
      VISIBILITY_CHIP_HEIGHT,
      VISIBILITY_CHIP_RADIUS
    );
    chipBg.lineStyle(
      selected ? 2 : 1,
      selected ? COLORS.accentCyanInt : COLORS.borderSubtleInt,
      1
    );
    chipBg.strokeRoundedRect(
      x - VISIBILITY_CHIP_WIDTH / 2,
      y - VISIBILITY_CHIP_HEIGHT / 2,
      VISIBILITY_CHIP_WIDTH,
      VISIBILITY_CHIP_HEIGHT,
      VISIBILITY_CHIP_RADIUS
    );
    chipBg.setDepth(PANEL_DEPTH);
    this.visibilityChipObjects.push(chipBg);

    const hit = this.scene.add
      .rectangle(
        x,
        y,
        VISIBILITY_CHIP_WIDTH,
        VISIBILITY_CHIP_HEIGHT,
        0x000000,
        0
      )
      .setInteractive({ cursor: 'pointer' })
      .setDepth(PANEL_DEPTH + 1);
    hit.on('pointerdown', () => {
      if (this.selectedIsPublic === value) return;
      this.selectedIsPublic = value;
      this.renderVisibilityChips();
    });
    this.visibilityChipObjects.push(hit);

    const text = this.scene.add
      .text(x, y, label, {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: selected ? COLORS.accentCyanHex : COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.visibilityChipObjects.push(text);
  }

  // --- Seats chips (FFA only) ---

  private renderSeatsChips(): void {
    for (const o of this.seatsChipObjects) o.destroy();
    this.seatsChipObjects = [];

    const cx = DESIGN_WIDTH / 2;
    const rowY = this.seatsSectionY + 50;
    const totalWidth =
      SEATS_OPTIONS.length * SEATS_CHIP_WIDTH +
      (SEATS_OPTIONS.length - 1) * SEATS_CHIP_GAP;
    const firstChipX = cx - totalWidth / 2 + SEATS_CHIP_WIDTH / 2;

    SEATS_OPTIONS.forEach((seats, i) => {
      const chipX = firstChipX + i * (SEATS_CHIP_WIDTH + SEATS_CHIP_GAP);
      this.drawSeatsChip(chipX, rowY, seats);
    });
  }

  private drawSeatsChip(x: number, y: number, value: number): void {
    const selected = this.selectedTargetSeats === value;

    const chipBg = this.scene.add.graphics();
    chipBg.fillStyle(
      selected ? COLORS.buttonBgPrimaryInt : COLORS.bgElevatedInt,
      1
    );
    chipBg.fillRoundedRect(
      x - SEATS_CHIP_WIDTH / 2,
      y - SEATS_CHIP_HEIGHT / 2,
      SEATS_CHIP_WIDTH,
      SEATS_CHIP_HEIGHT,
      SEATS_CHIP_RADIUS
    );
    chipBg.lineStyle(
      selected ? 2 : 1,
      selected ? COLORS.accentCyanInt : COLORS.borderSubtleInt,
      1
    );
    chipBg.strokeRoundedRect(
      x - SEATS_CHIP_WIDTH / 2,
      y - SEATS_CHIP_HEIGHT / 2,
      SEATS_CHIP_WIDTH,
      SEATS_CHIP_HEIGHT,
      SEATS_CHIP_RADIUS
    );
    chipBg.setDepth(PANEL_DEPTH);
    this.seatsChipObjects.push(chipBg);

    const hit = this.scene.add
      .rectangle(x, y, SEATS_CHIP_WIDTH, SEATS_CHIP_HEIGHT, 0x000000, 0)
      .setInteractive({ cursor: 'pointer' })
      .setDepth(PANEL_DEPTH + 1);
    hit.on('pointerdown', () => {
      if (this.selectedTargetSeats === value) return;
      this.selectedTargetSeats = value;
      this.renderSeatsChips();
    });
    this.seatsChipObjects.push(hit);

    const text = this.scene.add
      .text(x, y, String(value), {
        fontFamily: FONT_HEADER,
        fontSize: '26px',
        color: selected ? COLORS.accentCyanHex : COLORS.textPrimary,
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.seatsChipObjects.push(text);
  }

  // --- AI personality rows ---

  private renderPersonalityRows(): void {
    for (const o of this.personalityChipObjects) o.destroy();
    this.personalityChipObjects = [];

    const cx = DESIGN_WIDTH / 2;
    const rowGap = 70;
    const seatLabels = ['AI 1', 'AI 2'];

    for (let seatIdx = 0; seatIdx < 2; seatIdx++) {
      const rowY = this.personalitySectionY + 40 + seatIdx * rowGap;

      const seatLabel = this.scene.add
        .text(
          cx - PANEL_WIDTH / 2 + 40,
          rowY,
          seatLabels[seatIdx] ?? '',
          {
            fontFamily: FONT_HEADER,
            fontSize: '18px',
            color: COLORS.textPrimary,
            fontStyle: '700',
          }
        )
        .setLetterSpacing(3)
        .setOrigin(0, 0.5)
        .setDepth(PANEL_DEPTH);
      this.personalityChipObjects.push(seatLabel);

      // Three personality chips, right-aligned in the row.
      const chipsRight = cx + PANEL_WIDTH / 2 - 40;
      const totalChipsWidth =
        PERSONALITIES.length * PERSONALITY_CHIP_WIDTH +
        (PERSONALITIES.length - 1) * PERSONALITY_CHIP_GAP;
      const firstChipX =
        chipsRight - totalChipsWidth + PERSONALITY_CHIP_WIDTH / 2;

      PERSONALITIES.forEach((personality, i) => {
        const chipX =
          firstChipX +
          i * (PERSONALITY_CHIP_WIDTH + PERSONALITY_CHIP_GAP);
        this.drawPersonalityChip(chipX, rowY, seatIdx as 0 | 1, personality.id);
      });
    }
  }

  private drawPersonalityChip(
    x: number,
    y: number,
    seatIdx: 0 | 1,
    personality: PersonalityId
  ): void {
    const selected = this.selectedPersonalities[seatIdx] === personality;

    const chipBg = this.scene.add.graphics();
    chipBg.fillStyle(
      selected ? COLORS.buttonBgPrimaryInt : COLORS.bgElevatedInt,
      1
    );
    chipBg.fillRoundedRect(
      x - PERSONALITY_CHIP_WIDTH / 2,
      y - PERSONALITY_CHIP_HEIGHT / 2,
      PERSONALITY_CHIP_WIDTH,
      PERSONALITY_CHIP_HEIGHT,
      PERSONALITY_CHIP_RADIUS
    );
    chipBg.lineStyle(
      selected ? 2 : 1,
      selected ? COLORS.accentCyanInt : COLORS.borderSubtleInt,
      1
    );
    chipBg.strokeRoundedRect(
      x - PERSONALITY_CHIP_WIDTH / 2,
      y - PERSONALITY_CHIP_HEIGHT / 2,
      PERSONALITY_CHIP_WIDTH,
      PERSONALITY_CHIP_HEIGHT,
      PERSONALITY_CHIP_RADIUS
    );
    chipBg.setDepth(PANEL_DEPTH);
    this.personalityChipObjects.push(chipBg);

    const hit = this.scene.add
      .rectangle(
        x,
        y,
        PERSONALITY_CHIP_WIDTH,
        PERSONALITY_CHIP_HEIGHT,
        0x000000,
        0
      )
      .setInteractive({ cursor: 'pointer' })
      .setDepth(PANEL_DEPTH + 1);
    hit.on('pointerdown', () => {
      if (this.selectedPersonalities[seatIdx] === personality) return;
      this.selectedPersonalities[seatIdx] = personality;
      this.renderPersonalityRows();
    });
    this.personalityChipObjects.push(hit);

    const label = this.scene.add
      .text(x, y, personality.toUpperCase(), {
        fontFamily: FONT_HEADER,
        fontSize: '14px',
        color: selected ? COLORS.accentCyanHex : COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5)
      .setDepth(PANEL_DEPTH);
    this.personalityChipObjects.push(label);
  }

  // --- Lifecycle ---

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const o of this.objects) o.destroy();
    for (const o of this.mapCardObjects) o.destroy();
    for (const o of this.visibilityChipObjects) o.destroy();
    for (const o of this.seatsChipObjects) o.destroy();
    for (const o of this.personalityChipObjects) o.destroy();
    for (const b of this.buttons) b.destroy();
    this.objects.length = 0;
    this.mapCardObjects.length = 0;
    this.visibilityChipObjects.length = 0;
    this.seatsChipObjects.length = 0;
    this.personalityChipObjects.length = 0;
    this.buttons.length = 0;
  }
}
